import { useEffect, useMemo, useRef, useState, type CSSProperties } from "react";
import {
  PiArrowClockwiseBold,
  PiDownloadSimpleBold,
  PiFolderBold,
  PiFolderPlusBold,
  PiMagnifyingGlassBold,
  PiMicrophoneBold,
  PiPencilSimpleBold,
  PiPlayBold,
  PiSparkleBold,
  PiStopBold,
  PiTrashBold,
  PiUploadSimpleBold,
} from "react-icons/pi";
import { Karaoke } from "./Karaoke";
import { SplitHandle, loadStoredPx, saveStoredPx } from "./SplitHandle";
import { FINISH_PRESETS, loadFinishPreset, saveFinishPreset } from "../lib/finish";
import {
  INBOX_ID,
  addFolder,
  formatBytes,
  formatTakeWhen,
  loadCurrentFolderId,
  loadFolders,
  renameFolder as renameFolderList,
  saveCurrentFolderId,
  saveFolders,
  takeFolderId,
  type FinishPresetId,
  type TakeFolder,
  type TakeItem,
  type TakeListen,
} from "../lib/takesStore";

type Props = {
  src: string | null;
  status: string;
  generating: boolean;
  finishing: boolean;
  reworking?: boolean;
  finishEnabled: boolean;
  open: boolean;
  playNonce: number;
  takes: TakeItem[];
  activeId: string | null;
  onGenerate: (opts?: {
    keepSeed?: boolean;
    folderId?: string;
    karaoke?: boolean;
    lyrics?: string;
    title?: string;
  }) => void;
  onStop: () => void;
  onFinish: (take: TakeItem, preset: FinishPresetId) => void;
  onRework: (take: TakeItem, lyrics: string) => void;
  onListen: (take: TakeItem, listen: TakeListen) => void;
  onPlayTake: (take: TakeItem) => void;
  onSeedTake: (take: TakeItem) => void;
  onLoadTake: (take: TakeItem) => void;
  onDeleteTakes: (ids: string[]) => void;
  onRenameTake: (take: TakeItem, title: string) => void;
  onMoveTakes: (ids: string[], folderId: string) => void;
  onImportFiles: (files: File[], folderId: string) => void;
  onKaraokeBed?: (take: TakeItem) => void;
  onSyncLyrics?: (take: TakeItem) => void;
  studioLyrics?: string;
};

type SortKey = "title" | "createdAt" | "duration" | "seed" | "bytes";

const REWORK_H_KEY = "musicforge.reworkH.v1";
const REWORK_MIN = 160;
const REWORK_FILES_MIN = 96;

function fmtElapsed(sec: number): string {
  const s = Math.max(0, Math.floor(sec));
  const m = Math.floor(s / 60);
  return `${m}:${String(s % 60).padStart(2, "0")}`;
}

export function Player({
  src,
  status,
  generating,
  finishing,
  reworking = false,
  finishEnabled,
  open,
  playNonce,
  takes,
  activeId,
  onGenerate,
  onStop,
  onFinish,
  onRework,
  onListen,
  onPlayTake,
  onSeedTake,
  onLoadTake,
  onDeleteTakes,
  onRenameTake,
  onMoveTakes,
  onImportFiles,
  onKaraokeBed,
  onSyncLyrics,
  studioLyrics = "",
}: Props) {
  const audioRef = useRef<HTMLAudioElement | null>(null);
  const importRef = useRef<HTMLInputElement | null>(null);
  const playerRef = useRef<HTMLDivElement | null>(null);
  const makingFolder = useRef(false);
  const [elapsed, setElapsed] = useState(0);
  const [folders, setFolders] = useState<TakeFolder[]>(() => loadFolders());
  const [folderId, setFolderId] = useState(() => loadCurrentFolderId());
  const [query, setQuery] = useState("");
  const [sort, setSort] = useState<{ key: SortKey; dir: "asc" | "desc" }>({
    key: "createdAt",
    dir: "desc",
  });
  const [selected, setSelected] = useState<Set<string>>(() => new Set());
  const [newFolderOpen, setNewFolderOpen] = useState(false);
  const [newFolderName, setNewFolderName] = useState("");
  const [renameId, setRenameId] = useState<string | null>(null);
  const [renameDraft, setRenameDraft] = useState("");
  const [folderRenameId, setFolderRenameId] = useState<string | null>(null);
  const [folderDraft, setFolderDraft] = useState("");
  const [preset, setPreset] = useState<FinishPresetId>(() => loadFinishPreset());
  const [karaoke, setKaraoke] = useState(false);
  const [reworkOpen, setReworkOpen] = useState(false);
  const [reworkLyrics, setReworkLyrics] = useState("");
  const [reworkH, setReworkH] = useState(() => {
    const vh = typeof window === "undefined" ? 720 : window.innerHeight;
    return loadStoredPx(REWORK_H_KEY, Math.round(vh * 0.42), REWORK_MIN, Math.round(vh * 0.72));
  });
  const busy = generating || finishing || reworking;
  const seekRef = useRef<{ t: number; play: boolean } | null>(null);

  const active = useMemo(
    () => takes.find((t) => t.id === activeId) || takes.find((t) => t.objectUrl === src) || null,
    [takes, activeId, src],
  );

  const visible = useMemo(() => {
    const q = query.trim().toLowerCase();
    let rows = takes.filter((t) => (folderId === "all" ? true : takeFolderId(t) === folderId));
    if (q) {
      rows = rows.filter((t) => {
        const hay = `${t.title} ${t.seed} ${t.lyrics || ""}`.toLowerCase();
        return hay.includes(q);
      });
    }
    const dir = sort.dir === "asc" ? 1 : -1;
    return [...rows].sort((a, b) => {
      const ka = a[sort.key];
      const kb = b[sort.key];
      if (sort.key === "title") return dir * String(a.title).localeCompare(String(b.title));
      if (sort.key === "createdAt") return dir * String(a.createdAt).localeCompare(String(b.createdAt));
      return dir * (Number(ka || 0) - Number(kb || 0));
    });
  }, [takes, folderId, query, sort]);

  const counts = useMemo(() => {
    const map = new Map<string, number>();
    for (const t of takes) {
      const id = takeFolderId(t);
      map.set(id, (map.get(id) || 0) + 1);
    }
    return map;
  }, [takes]);

  const listen: TakeListen =
    active?.listen === "finished" && active.finishedUrl ? "finished" : "original";
  const playSrc =
    karaoke && (active?.karaokeBedUrl || active?.karaoke?.source === "gen")
      ? active.karaokeBedUrl || active.objectUrl || src
      : listen === "finished" && active?.finishedUrl
        ? active.finishedUrl
        : active?.objectUrl || src;

  useEffect(() => {
    if (!open) audioRef.current?.pause();
  }, [open]);

  useEffect(() => {
    if (busy) audioRef.current?.pause();
  }, [busy]);

  useEffect(() => {
    if (!playNonce || busy || !src) return;
    audioRef.current?.play().catch(() => {
      /* gesture */
    });
  }, [playNonce, busy, src]);

  useEffect(() => {
    const el = audioRef.current;
    const pending = seekRef.current;
    if (!el || !pending || !playSrc) return;
    const apply = () => {
      try {
        el.currentTime = pending.t;
      } catch {
        /* */
      }
      if (pending.play) el.play().catch(() => undefined);
      seekRef.current = null;
    };
    if (el.readyState >= 1) apply();
    else el.addEventListener("loadedmetadata", apply, { once: true });
  }, [playSrc]);

  useEffect(() => {
    if (!busy) {
      setElapsed(0);
      return;
    }
    const t0 = Date.now();
    const id = window.setInterval(() => setElapsed((Date.now() - t0) / 1000), 250);
    return () => window.clearInterval(id);
  }, [busy]);

  useEffect(() => {
    saveCurrentFolderId(folderId === "all" ? INBOX_ID : folderId);
  }, [folderId]);

  useEffect(() => {
    setReworkLyrics(active?.lyrics || studioLyrics || "");
  }, [active?.id, active?.lyrics, studioLyrics]);

  useEffect(() => saveStoredPx(REWORK_H_KEY, reworkH), [reworkH]);

  const reworkMax = () => {
    const root = playerRef.current;
    if (!root) return 480;
    let used = 0;
    const kids = Array.from(root.children) as HTMLElement[];
    for (const child of kids) {
      if (child.classList.contains("mf-files-table-wrap")) {
        used += REWORK_FILES_MIN;
        continue;
      }
      if (child.classList.contains("mf-rework")) {
        const bar = child.querySelector(".mf-rework-bar") as HTMLElement | null;
        const split = child.querySelector(".mf-split") as HTMLElement | null;
        used += (bar?.offsetHeight ?? 26) + (split?.offsetHeight ?? 12);
        continue;
      }
      used += child.offsetHeight;
    }
    const gaps = 4 * Math.max(0, kids.length - 1);
    const pad = 12;
    return Math.max(REWORK_MIN, Math.round(root.clientHeight - used - gaps - pad));
  };

  const openRework = (open: boolean) => {
    setReworkOpen(open);
    if (!open) return;
    const box = playerRef.current?.clientHeight ?? (typeof window === "undefined" ? 720 : window.innerHeight);
    const cap = reworkMax();
    const wide = Math.max(280, Math.round(box * 0.55));
    const headroom = 56;
    setReworkH((h) => {
      const want = Math.max(h, Math.min(cap, wide));
      if (cap - want < 24) return Math.max(REWORK_MIN, Math.min(want, cap - headroom));
      return want;
    });
  };

  const pickVersion = (next: TakeListen) => {
    if (!active) return;
    if (next === "finished" && !active.finishedUrl) {
      onFinish(active, preset);
      return;
    }
    const el = audioRef.current;
    seekRef.current = {
      t: el && Number.isFinite(el.currentTime) ? el.currentTime : 0,
      play: Boolean(el && !el.paused && !el.ended),
    };
    onListen(active, next);
  };
  const fileName = `${(active?.title || "musicforge").replace(/[^\w.-]+/g, "_").slice(0, 48)}${
    listen === "finished" ? "_finished" : ""
  }.wav`;
  const destFolder = folderId === "all" ? INBOX_ID : folderId;
  const playSrcRef = useRef(playSrc);
  if (playSrcRef.current !== playSrc && karaoke && audioRef.current) {
    const el = audioRef.current;
    seekRef.current = {
      t: Number.isFinite(el.currentTime) ? el.currentTime : 0,
      play: !el.paused && !el.ended,
    };
  }
  playSrcRef.current = playSrc;
  const selectedIds = [...selected].filter((id) => visible.some((t) => t.id === id));

  const toggleSort = (key: SortKey) => {
    setSort((s) => (s.key === key ? { key, dir: s.dir === "asc" ? "desc" : "asc" } : { key, dir: key === "title" ? "asc" : "desc" }));
  };

  const toggleSel = (id: string) => {
    setSelected((cur) => {
      const next = new Set(cur);
      if (next.has(id)) next.delete(id);
      else next.add(id);
      return next;
    });
  };

  const toggleAll = () => {
    if (selectedIds.length === visible.length) setSelected(new Set());
    else setSelected(new Set(visible.map((t) => t.id)));
  };

  const commitFileRename = (take: TakeItem) => {
    if (renameDraft.trim()) onRenameTake(take, renameDraft.trim());
    setRenameId(null);
  };

  const createFolder = () => {
    if (makingFolder.current) return;
    makingFolder.current = true;
    const label = newFolderName.trim();
    setNewFolderOpen(false);
    setNewFolderName("");
    if (label) {
      const next = addFolder(folders, label);
      setFolders(next);
      const made = next[next.length - 1];
      if (made) {
        setFolderId(made.id);
        saveCurrentFolderId(made.id);
      }
    }
    window.setTimeout(() => {
      makingFolder.current = false;
    }, 0);
  };

  const commitFolderRename = (id: string) => {
    setFolders((cur) => renameFolderList(cur, id, folderDraft));
    setFolderRenameId(null);
  };

  const deleteFolder = (id: string) => {
    if (id === INBOX_ID) return;
    const ids = takes.filter((t) => takeFolderId(t) === id).map((t) => t.id);
    setFolders((cur) => {
      const next = cur.filter((f) => f.id !== id);
      saveFolders(next);
      return next;
    });
    if (ids.length) onMoveTakes(ids, INBOX_ID);
    if (folderId === id) setFolderId(INBOX_ID);
  };

  return (
    <div
      ref={playerRef}
      className={`mf-player${reworkOpen ? " rework-open" : ""}`}
      style={reworkOpen ? ({ ["--mf-rework-h"]: `${Math.min(reworkH, reworkMax())}px` } as CSSProperties) : undefined}
    >
      <div className="mf-player-head">
        <strong className="mf-player-now">{active ? active.title : "Player"}</strong>
        {generating ? (
          <span className="mf-live">Making song {fmtElapsed(elapsed)}</span>
        ) : finishing ? (
          <span className="mf-live">{status || "Working…"} {fmtElapsed(elapsed)}</span>
        ) : status ? (
          <span className="mf-player-status" title={status}>
            {status}
          </span>
        ) : null}
        <div className="mf-player-actions">
          {busy ? (
            <button type="button" className="mf-btn ghost" onClick={onStop}>
              <PiStopBold /> Stop
            </button>
          ) : (
            <>
              {active ? (
                <button
                  type="button"
                  className="mf-btn ghost"
                  title="Same lyrics, caption, and seed"
                  onClick={() => onGenerate({ keepSeed: true, folderId: destFolder })}
                >
                  <PiArrowClockwiseBold /> Retry
                </button>
              ) : null}
              <button
                type="button"
                className="mf-btn ghost"
                title="Game-night lyrics on the big screen. Make karaoke from the start for a music-only bed."
                onClick={() => setKaraoke(true)}
              >
                <PiMicrophoneBold /> Karaoke
              </button>
              <button
                type="button"
                className="mf-btn ghost"
                title="Generate a music-only karaoke take from Mixing bowl + lyrics. No lead vocal."
                disabled={busy}
                onClick={() =>
                  onGenerate({
                    karaoke: true,
                    keepSeed: Boolean(active),
                    folderId: destFolder,
                    lyrics: active?.lyrics || studioLyrics || undefined,
                    title: active?.title,
                  })
                }
              >
                Make karaoke
              </button>
              <button type="button" className="mf-btn primary lg" onClick={() => onGenerate({ folderId: destFolder })}>
                Make song
              </button>
            </>
          )}
        </div>
      </div>

      {playSrc ? (
        <div className="mf-player-body mf-version-card">
          {finishEnabled ? (
            <div className="mf-version-tabs" role="tablist" aria-label="Original or finished">
              <button
                type="button"
                role="tab"
                aria-selected={listen === "original"}
                className={listen === "original" ? "on" : ""}
                disabled={!active}
                onClick={() => pickVersion("original")}
              >
                Original
              </button>
              <button
                type="button"
                role="tab"
                aria-selected={listen === "finished"}
                className={listen === "finished" ? "on" : ""}
                disabled={!active || busy}
                onClick={() => pickVersion("finished")}
              >
                {active?.finishedUrl ? "Finished" : finishing ? "Finishing…" : "Finished"}
              </button>
            </div>
          ) : null}
          <div className="mf-player-transport">
            <audio ref={audioRef} src={playSrc} controls className="mf-audio" />
            <a className="mf-btn ghost" href={playSrc} download={fileName} title="Download this version">
              <PiDownloadSimpleBold /> {listen === "finished" ? "Finished WAV" : "Original WAV"}
            </a>
          </div>
          {finishEnabled ? (
            <p className="mf-version-caption">
              {listen === "finished"
                ? `Finished · ${active?.finish?.preset || preset} master — louder, brighter, glued. Switch to Original to A/B.`
                : active?.finishedUrl
                  ? "Unmastered take. Switch to Finished to hear the master."
                  : "Unmastered take. Open Finished (or Make finished) to build the master."}
            </p>
          ) : null}
        </div>
      ) : (
        <p className="mf-hint">{generating ? "Music 3 is writing the take…" : "Make song, or import a file into the shelf."}</p>
      )}

      {finishEnabled ? (
      <div className="mf-finish" aria-label="Production finish">
        <div className="mf-finish-presets" role="group" aria-label="Upload finish preset">
          {FINISH_PRESETS.map((p) => (
            <button
              key={p.id}
              type="button"
              className={`mf-chip${preset === p.id ? " on" : ""}`}
              title={p.blurb}
              disabled={busy}
              onClick={() => {
                setPreset(p.id);
                saveFinishPreset(p.id);
              }}
            >
              {p.label}
            </button>
          ))}
        </div>
        <button
          type="button"
          className="mf-btn"
          disabled={busy || !active?.objectUrl}
          title="Make the Finished version. Original stays. A/B with the tabs on the player."
          onClick={() => active && onFinish(active, preset)}
        >
          <PiSparkleBold /> {active?.finishedUrl ? "Remake finished" : "Make finished"}
        </button>
      </div>
      ) : null}

      <div className="mf-rework" aria-label="Rework lyrics">
        <div className="mf-rework-bar">
          <button
            type="button"
            className={`mf-btn${reworkOpen ? " on" : ""}`}
            disabled={busy || !active?.objectUrl}
            aria-expanded={reworkOpen}
            title="Change a few words on this take. ACE-Step re-sings the cover; original stays."
            onClick={() => openRework(!reworkOpen)}
          >
            <PiPencilSimpleBold /> Rework lyrics
          </button>
          {reworkOpen ? (
            <button
              type="button"
              className="mf-btn primary"
              disabled={busy || !active?.objectUrl || !reworkLyrics.trim()}
              onClick={() => active && onRework(active, reworkLyrics)}
            >
              Rework
            </button>
          ) : null}
        </div>
        {reworkOpen ? (
          <>
            <div className="mf-rework-edit">
              <textarea
                className="mf-input mf-rework-lyrics"
                value={reworkLyrics}
                onChange={(e) => setReworkLyrics(e.target.value)}
                disabled={busy}
                aria-label="Reworked lyrics"
                placeholder="Keep section tags and syllable count close to the original."
              />
            </div>
            <SplitHandle
              label="Resize rework lyrics"
              value={reworkH}
              min={REWORK_MIN}
              max={1600}
              getMax={reworkMax}
              onChange={setReworkH}
              onReset={() => setReworkH(Math.max(REWORK_MIN, Math.round(reworkMax() * 0.72)))}
              invert={false}
            />
          </>
        ) : null}
      </div>

      <div className="mf-files-bar">
        <div className="mf-folder-row" role="tablist" aria-label="Folders">
          <button
            type="button"
            className={`mf-chip${folderId === "all" ? " on" : ""}`}
            onClick={() => setFolderId("all")}
          >
            All {takes.length}
          </button>
          {folders.map((f) => (
            <span key={f.id} className="mf-folder-chip">
              {folderRenameId === f.id ? (
                <input
                  className="mf-input mf-folder-input"
                  value={folderDraft}
                  autoFocus
                  aria-label="Folder name"
                  onChange={(e) => setFolderDraft(e.target.value)}
                  onBlur={() => commitFolderRename(f.id)}
                  onKeyDown={(e) => {
                    if (e.key === "Enter") commitFolderRename(f.id);
                    if (e.key === "Escape") setFolderRenameId(null);
                  }}
                />
              ) : (
                <button
                  type="button"
                  className={`mf-chip${folderId === f.id ? " on" : ""}`}
                  onClick={() => setFolderId(f.id)}
                  onDoubleClick={() => {
                    if (f.id === INBOX_ID) return;
                    setFolderRenameId(f.id);
                    setFolderDraft(f.name);
                  }}
                >
                  <PiFolderBold /> {f.name} {counts.get(f.id) || 0}
                </button>
              )}
              {f.id !== INBOX_ID ? (
                <button
                  type="button"
                  className="mf-x"
                  aria-label={`Delete folder ${f.name}`}
                  title="Delete folder (files move to Inbox)"
                  onClick={() => deleteFolder(f.id)}
                >
                  ×
                </button>
              ) : null}
            </span>
          ))}
          {newFolderOpen ? (
            <input
              className="mf-input mf-folder-input"
              value={newFolderName}
              autoFocus
              placeholder="Folder name"
              aria-label="New folder name"
              onChange={(e) => setNewFolderName(e.target.value)}
              onBlur={() => (newFolderName.trim() ? createFolder() : setNewFolderOpen(false))}
              onKeyDown={(e) => {
                if (e.key === "Enter") createFolder();
                if (e.key === "Escape") setNewFolderOpen(false);
              }}
            />
          ) : (
            <button type="button" className="mf-chip" onClick={() => setNewFolderOpen(true)}>
              <PiFolderPlusBold /> New folder
            </button>
          )}
        </div>
        <div className="mf-files-tools">
          <label className="mf-files-search">
            <PiMagnifyingGlassBold />
            <input
              className="mf-input"
              value={query}
              onChange={(e) => setQuery(e.target.value)}
              placeholder="Search files"
              aria-label="Search files"
            />
          </label>
          <input
            ref={importRef}
            type="file"
            accept="audio/*,.wav,.mp3,.flac,.ogg,.m4a"
            multiple
            hidden
            onChange={(e) => {
              const files = [...(e.target.files || [])];
              e.target.value = "";
              if (files.length) onImportFiles(files, destFolder);
            }}
          />
          <button type="button" className="mf-btn" onClick={() => importRef.current?.click()}>
            <PiUploadSimpleBold /> Import
          </button>
        </div>
      </div>

      {selectedIds.length ? (
        <div className="mf-files-bulk">
          <span>{selectedIds.length} selected</span>
          <select
            className="mf-input mf-files-move"
            aria-label="Move to folder"
            defaultValue=""
            onChange={(e) => {
              const id = e.target.value;
              e.currentTarget.value = "";
              if (id) {
                onMoveTakes(selectedIds, id);
                setSelected(new Set());
              }
            }}
          >
            <option value="" disabled>
              Move to…
            </option>
            {folders.map((f) => (
              <option key={f.id} value={f.id}>
                {f.name}
              </option>
            ))}
          </select>
          <button
            type="button"
            className="mf-btn ghost"
            onClick={() => {
              for (const t of takes) {
                if (!selectedIds.includes(t.id) || !t.objectUrl) continue;
                const a = document.createElement("a");
                a.href = t.objectUrl;
                a.download = `${t.title.replace(/[^\w.-]+/g, "_").slice(0, 48)}.wav`;
                a.click();
              }
            }}
          >
            <PiDownloadSimpleBold /> Download
          </button>
          <button
            type="button"
            className="mf-btn ghost"
            onClick={() => {
              onDeleteTakes(selectedIds);
              setSelected(new Set());
            }}
          >
            <PiTrashBold /> Delete
          </button>
        </div>
      ) : null}

      <div className="mf-files-table-wrap">
        {visible.length ? (
          <table className="mf-files-table">
            <thead>
              <tr>
                <th className="mf-files-check">
                  <input
                    type="checkbox"
                    checked={visible.length > 0 && selectedIds.length === visible.length}
                    onChange={toggleAll}
                    aria-label="Select all files"
                  />
                </th>
                <th>
                  <button type="button" onClick={() => toggleSort("title")}>
                    Name
                  </button>
                </th>
                <th className="mf-files-hide-sm">
                  <button type="button" onClick={() => toggleSort("createdAt")}>
                    Date
                  </button>
                </th>
                <th>
                  <button type="button" onClick={() => toggleSort("duration")}>
                    Time
                  </button>
                </th>
                <th className="mf-files-hide-sm">
                  <button type="button" onClick={() => toggleSort("seed")}>
                    Seed
                  </button>
                </th>
                <th className="mf-files-hide-sm">
                  <button type="button" onClick={() => toggleSort("bytes")}>
                    Size
                  </button>
                </th>
                <th>Actions</th>
              </tr>
            </thead>
            <tbody>
              {visible.map((t) => (
                <tr key={t.id} className={t.id === active?.id ? "on" : ""}>
                  <td className="mf-files-check">
                    <input
                      type="checkbox"
                      checked={selected.has(t.id)}
                      onChange={() => toggleSel(t.id)}
                      aria-label={`Select ${t.title}`}
                    />
                  </td>
                  <td>
                    {renameId === t.id ? (
                      <input
                        className="mf-input"
                        value={renameDraft}
                        autoFocus
                        aria-label="File name"
                        onChange={(e) => setRenameDraft(e.target.value)}
                        onBlur={() => commitFileRename(t)}
                        onKeyDown={(e) => {
                          if (e.key === "Enter") commitFileRename(t);
                          if (e.key === "Escape") setRenameId(null);
                        }}
                      />
                    ) : (
                      <button type="button" className="mf-file-name" onClick={() => onPlayTake(t)} onDoubleClick={() => {
                        setRenameId(t.id);
                        setRenameDraft(t.title);
                      }}>
                        {t.title}
                        <small>{t.kind === "import" ? "Imported" : "Generated"}</small>
                      </button>
                    )}
                  </td>
                  <td className="mf-files-hide-sm">{formatTakeWhen(t.createdAt)}</td>
                  <td>{t.duration}s</td>
                  <td className="mf-files-hide-sm">{t.seed || "—"}</td>
                  <td className="mf-files-hide-sm">{formatBytes(t.bytes)}</td>
                  <td>
                    <div className="mf-file-acts">
                      <button type="button" className="mf-chip" title="Play" onClick={() => onPlayTake(t)} aria-label={`Play ${t.title}`}>
                        <PiPlayBold />
                      </button>
                      <button type="button" className="mf-chip" title="Load lyrics + caption into Studio" onClick={() => onLoadTake(t)}>
                        Load
                      </button>
                      <button type="button" className="mf-chip" title="Use as seed" onClick={() => onSeedTake(t)}>
                        Seed
                      </button>
                      {t.objectUrl ? (
                        <a className="mf-chip" href={t.objectUrl} download={`${t.title.replace(/[^\w.-]+/g, "_").slice(0, 48)}.wav`} title="Download">
                          <PiDownloadSimpleBold />
                        </a>
                      ) : null}
                      <button type="button" className="mf-chip" title="Delete" onClick={() => onDeleteTakes([t.id])} aria-label={`Delete ${t.title}`}>
                        <PiTrashBold />
                      </button>
                    </div>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        ) : (
          <p className="mf-hint mf-files-empty">
            {query
              ? "No files match that search."
              : "This folder is empty. Make a song or Import audio."}
          </p>
        )}
      </div>
      {karaoke ? (
        <Karaoke
          title={active?.title || "Untitled"}
          lyrics={active?.syncedLyrics || active?.lyrics || studioLyrics}
          audio={audioRef.current}
          queue={
            selectedIds.length
              ? visible.filter((t) => selected.has(t.id))
              : visible
          }
          activeId={active?.id || null}
          bedReady={Boolean(active?.karaokeBedUrl || active?.karaoke?.source === "gen")}
          bedSource={active?.karaoke?.source || null}
          synced={Boolean(active?.syncedLyrics)}
          making={busy}
          onClose={() => setKaraoke(false)}
          onPlayTake={onPlayTake}
          onMakeKaraoke={() =>
            onGenerate({
              karaoke: true,
              keepSeed: Boolean(active),
              folderId: destFolder,
              lyrics: active?.lyrics || studioLyrics || undefined,
              title: active?.title,
            })
          }
          onMakeBed={active && onKaraokeBed ? () => onKaraokeBed(active) : undefined}
          onSyncLyrics={active && onSyncLyrics ? () => onSyncLyrics(active) : undefined}
        />
      ) : null}
    </div>
  );
}
